import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { Loader2, Search } from 'lucide-react';
import { toast } from 'sonner';
import { adminApi } from '@/lib/adminApi';
import { formatApiError } from '@/lib/api';

/**
 * Iter 41.35 — Create-advisor flow (hm_admin only).
 *
 * Collects name, email, phone, initial password, and a multi-select of
 * companies to seed the advisor's book. The dialog also surfaces an empty
 * `initial_password` checkbox so the admin can use the demo default and
 * have the modal display the resulting password hint after creation.
 */
export default function CreateAdvisorDialog({ open, onOpenChange, onCreated }) {
  const { t } = useTranslation();
  const [form, setForm] = useState({
    name: '', email: '', phone: '', initial_password: '',
  });
  const [pwAuto, setPwAuto] = useState(true);
  const [selected, setSelected] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [q, setQ] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    (async () => {
      try {
        const cos = await adminApi.listCompanies();
        setCompanies(cos || []);
      } catch {/* non-fatal */ }
    })();
    // reset form whenever the dialog opens
    setForm({ name: '', email: '', phone: '', initial_password: '' });
    setSelected([]);
    setPwAuto(true);
    setQ('');
  }, [open]);

  const filteredCompanies = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return companies;
    return companies.filter((c) => (c.name || '').toLowerCase().includes(needle));
  }, [companies, q]);

  const toggle = (id) => setSelected((s) =>
    s.includes(id) ? s.filter((x) => x !== id) : [...s, id]
  );

  const canSubmit = form.name.trim().length >= 2 && /.+@.+\..+/.test(form.email) && !saving;

  const submit = async () => {
    if (!canSubmit) return;
    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(),
        email: form.email.trim().toLowerCase(),
        phone: form.phone.trim() || undefined,
        initial_password: pwAuto ? undefined : form.initial_password,
        assigned_client_ids: selected,
      };
      const res = await adminApi.createAdvisor(payload);
      const hint = res?.temp_password || res?.initial_password_hint;
      toast.success(t('pages.advisors.createdToast', { defaultValue: 'Advisor created' }), {
        description: hint
          ? t('pages.advisors.createdHint', { password: hint, defaultValue: 'Temporary password (shown once): {{password}}' })
          : undefined,
        duration: 30000,
      });
      onCreated?.();
      onOpenChange(false);
    } catch (e) {
      toast.error(t('pages.advisors.createFailed', { defaultValue: 'Could not create advisor' }), {
        description: formatApiError(e),
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl" data-testid="create-advisor-dialog">
        <DialogHeader>
          <DialogTitle>{t('pages.advisors.create', { defaultValue: 'Create advisor' })}</DialogTitle>
          <DialogDescription>
            {t('pages.advisors.createSubtitle', { defaultValue: 'Add a new CTH advisor and optionally assign client companies.' })}
          </DialogDescription>
        </DialogHeader>

        <div className="grid sm:grid-cols-2 gap-4 mt-2">
          <div className="sm:col-span-2">
            <Label htmlFor="ca-name">{t('pages.advisors.field.name', { defaultValue: 'Full name' })} *</Label>
            <Input id="ca-name" value={form.name}
                   onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                   data-testid="ca-name" />
          </div>
          <div>
            <Label htmlFor="ca-email">{t('pages.advisors.field.email', { defaultValue: 'Email' })} *</Label>
            <Input id="ca-email" type="email" value={form.email}
                   onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                   data-testid="ca-email" />
          </div>
          <div>
            <Label htmlFor="ca-phone">{t('pages.advisors.field.phone', { defaultValue: 'Phone (optional)' })}</Label>
            <Input id="ca-phone" value={form.phone}
                   onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))}
                   data-testid="ca-phone" />
          </div>
          <div className="sm:col-span-2 space-y-2">
            <Label>{t('pages.advisors.field.password', { defaultValue: 'Initial password' })}</Label>
            <div className="flex items-center gap-2">
              <Checkbox id="ca-pw-auto" checked={pwAuto} onCheckedChange={(v) => setPwAuto(!!v)}
                        data-testid="ca-pw-auto" />
              <label htmlFor="ca-pw-auto" className="text-sm cursor-pointer">
                {t('pages.advisors.field.passwordAuto', { defaultValue: 'Auto-generate a secure temporary password' })}
              </label>
            </div>
            {!pwAuto && (
              <Input type="text" placeholder="Min 8 characters"
                     value={form.initial_password}
                     onChange={(e) => setForm((f) => ({ ...f, initial_password: e.target.value }))}
                     data-testid="ca-pw-custom" />
            )}
          </div>
        </div>

        {/* Multi-select client book */}
        <div className="mt-4 space-y-2" data-testid="ca-clients-section">
          <Label>{t('pages.advisors.field.clients', { defaultValue: 'Assigned clients (optional)' })}</Label>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder={t('pages.advisors.field.clientsSearchPh', { defaultValue: 'Search company…' })}
              value={q}
              onChange={(e) => setQ(e.target.value)}
              className="pl-9 h-9"
              data-testid="ca-clients-search"
            />
          </div>
          <div className="border rounded-lg max-h-56 overflow-auto" data-testid="ca-clients-list">
            {filteredCompanies.length === 0 ? (
              <p className="p-4 text-xs text-muted-foreground italic">
                {t('pages.advisors.field.noCompanies', { defaultValue: 'No companies match.' })}
              </p>
            ) : (
              filteredCompanies.map((c) => (
                <label key={c.id}
                       className="flex items-center gap-2 px-3 py-2 hover:bg-muted/40 cursor-pointer text-sm"
                       data-testid={`ca-client-row-${c.id}`}>
                  <Checkbox checked={selected.includes(c.id)}
                            onCheckedChange={() => toggle(c.id)}
                            data-testid={`ca-client-cb-${c.id}`} />
                  <span className="flex-1">{c.name}</span>
                </label>
              ))
            )}
          </div>
          {selected.length > 0 && (
            <Badge variant="secondary" className="text-xs" data-testid="ca-clients-count">
              {t('pages.advisors.field.clientsSelected', { count: selected.length, defaultValue: '{{count}} selected' })}
            </Badge>
          )}
        </div>

        <DialogFooter className="mt-4">
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={saving} data-testid="ca-cancel">
            {t('common.cancel', { defaultValue: 'Cancel' })}
          </Button>
          <Button onClick={submit} disabled={!canSubmit}
                  className="bg-gradient-primary hover:opacity-90"
                  data-testid="ca-submit">
            {saving
              ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> {t('common.saving', { defaultValue: 'Saving…' })}</>
              : t('pages.advisors.create', { defaultValue: 'Create advisor' })}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
